import * as React from 'react';

import {
    HiveDataSyncState,
    type HiveWorkingBranch,
    type HiveWorkingDocument,
} from '@gitbook/hive-client';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { HiveGlobalPresenceContext } from './HiveContext';
import { useHiveBranch } from './useHiveBranch';
import { useHiveClient } from './useHiveClient';

const HiveWorkingDocumentContext = React.createContext<HiveWorkingDocument | null>(null);

interface UseHiveWorkingDocumentOptions {
    branch?: HiveWorkingBranch | { contentId: string; branchId: string };
    containerType: 'pages' | 'reusable-contents';
    containerId: string;
    readOnly?: boolean;
}

/**
 * Provide a specific content branch document to the react tree.
 */
export function HiveWorkingDocumentProvider(
    props: React.PropsWithChildren<UseHiveWorkingDocumentOptions>
): React.ReactElement {
    const { children, ...options } = props;

    const branch = useHiveWorkingDocumentById(options);
    return (
        <HiveWorkingDocumentContext.Provider value={branch}>
            {children}
        </HiveWorkingDocumentContext.Provider>
    );
}

/**
 * Return the current Hive working document. Does not rerender when the HiveWorkingDocument changes.
 * If you need to rerender on change, consider useHiveWorkingDocumentValue for the editor value
 */
export function useHiveWorkingDocument(): HiveWorkingDocument | null {
    const current = React.useContext(HiveWorkingDocumentContext);
    return current;
}

/**
 * Connect to a Hive page document.
 */
export function useHiveWorkingDocumentById(
    options: UseHiveWorkingDocumentOptions
): HiveWorkingDocument {
    const client = useHiveClient();
    const defaultBranch = useHiveBranch();
    const branch = options.branch || defaultBranch;
    assertIsDefined(branch);

    const globalPresence = React.useContext(HiveGlobalPresenceContext);

    const page = (() => {
        if (options.containerType === 'pages') {
            return client.sync.pageDocument(
                branch.contentId,
                branch.branchId,
                options.containerId,
                {
                    readOnly: options.readOnly,
                }
            );
        }

        if (options.containerType === 'reusable-contents') {
            return client.sync.reusableContent(
                branch.contentId,
                branch.branchId,
                options.containerId,
                {
                    readOnly: options.readOnly,
                }
            );
        }

        assertNever(options.containerType);
    })();

    const promise = page.open();
    if (promise) {
        throw promise;
    }

    React.useEffect(() => {
        if (!globalPresence && process.env.NODE_ENV === 'development') {
            throw new Error(
                'No global presence has been set on the HiveProvider before creating data syncs.'
            );
        }

        if (globalPresence) {
            page.dataSync.setPresence({
                data: globalPresence,
            });
        }
    }, [page]);

    React.useEffect(() => {
        const keepAlive = page.keepAlive();
        return () => {
            // Clear the presence when the page is changed
            // Because the data sync stays active in the background, but we don't want to continue showing the cursor in the editor
            if (page.dataSync.state === HiveDataSyncState.OPEN) {
                page.dataSync.setPresence({ positions: {} });
            }
            keepAlive();
        };
    }, [page]);

    return page;
}
